import { Controller } from "@hotwired/stimulus"

// Desktop / mobile tabs for parent-SKU pricing page SEO. Both panels stay
// in the form so a hidden tab still saves. Blank mobile fields are stored
// empty; the public page falls back to desktop.
export default class extends Controller {
  static targets = ["panel", "tab"]
  static values = {
    defaultTitle: String,
    titleLimit: { type: Number, default: 60 }
  }

  connect() {
    this.panelTargets.forEach((panel) => this.refreshPanel(panel))
  }

  show(event) {
    event.preventDefault()
    const viewport = event.currentTarget.dataset.viewport

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.viewport !== viewport)
    })

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.viewport === viewport
      tab.setAttribute("aria-selected", String(active))
      tab.classList.toggle("bg-white", active)
      tab.classList.toggle("text-gray-900", active)
      tab.classList.toggle("shadow", active)
      tab.classList.toggle("text-gray-500", !active)
    })
  }

  refresh(event) {
    const panel = event.target.closest("[data-pricing-page-seo-target='panel']")
    if (panel) this.refreshPanel(panel)
  }

  refreshPanel(panel) {
    this.#updateTitle(panel)
    this.#updateCount(panel, "intro")
    this.#updateCount(panel, "about")
  }

  #updateTitle(panel) {
    const input = panel.querySelector("[data-field='title']")
    const count = panel.querySelector("[data-count='title']")
    const status = panel.querySelector("[data-status='title']")
    const preview = panel.querySelector("[data-preview='title']")
    if (!input || !count || !status || !preview) return

    const length = input.value.length
    const limit = this.titleLimitValue
    count.textContent = `${length} / ${limit}`
    count.classList.toggle("text-amber-600", length > limit)
    count.classList.toggle("text-gray-500", length <= limit)

    status.classList.remove("text-green-700", "text-amber-700", "text-gray-500")
    preview.classList.remove("text-gray-400", "text-blue-800")

    if (length === 0) {
      status.textContent = panel.dataset.viewport === "mobile" ? "Uses the desktop title." : "Blank titles use the default."
      status.classList.add("text-gray-500")
      preview.textContent = panel.dataset.viewport === "mobile" ? this.#desktopTitle() : this.defaultTitleValue
      preview.classList.add("text-gray-400")
    } else if (length <= limit) {
      status.textContent = "✓ Good length for search results."
      status.classList.add("text-green-700")
      preview.textContent = input.value
      preview.classList.add("text-blue-800")
    } else {
      status.textContent = "May be cut off in search results."
      status.classList.add("text-amber-700")
      preview.textContent = input.value
      preview.classList.add("text-blue-800")
    }
  }

  #updateCount(panel, field) {
    const input = panel.querySelector(`[data-field='${field}']`)
    const count = panel.querySelector(`[data-count='${field}']`)
    if (!input || !count) return

    const length = input.value.length
    count.textContent = length === 1 ? "1 character" : `${length} characters`
  }

  #desktopTitle() {
    const desktop = this.panelTargets.find((panel) => panel.dataset.viewport === "desktop")
    const input = desktop?.querySelector("[data-field='title']")
    const typed = input?.value?.trim()
    return typed || this.defaultTitleValue
  }
}
